import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { LocalStorageService } from './localStorage.service';
import { LanguageService } from './language.service';
import { TimeStampInterface } from 'src/app/table-bookings/interface/table-bookings.interface';


// Load the plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

@Injectable({
  providedIn: 'root',
})
export class DateService {
  constructor(
    private _localStorage: LocalStorageService,
    private _languageService: LanguageService
  ) {}

  convertUTCtoDate(time: string | Date): string {
    const tz = this._localStorage.get('time_zone');
    const date = dayjs(time).tz(tz);
    return date.isValid() ? date.format('YYYY-MM-DD') : '';
  }

  convertReadableDate(time) {
    return dayjs
      .tz(dayjs(time, 'YYYY-MM-DD'), this._localStorage.get('time_zone'))
      .format('DD MMM YYYY');
  }

  formatDate(date) {
    return dayjs
      .tz(dayjs(date, 'YYYY-MM-DD'), this._localStorage.get('time_zone'))
      .format('DD MMM YYYY');
  }

  returnDateRangeText(date) {
    if (date) {
      if (date.startDate != date.endDate) {
        return `${this.convertReadableDate(
          date.startDate
        )} - ${this.convertReadableDate(date.endDate)}`;
      } else return `${this.convertReadableDate(date.startDate)}`;
    }
  }

  areTimesOnSameDay(startTime, endTime): boolean {
    const startDateTime = new Date(startTime);
    const endDateTime = new Date(endTime);
    const startHour = startDateTime.getHours();
    const endHour = endDateTime.getHours();

    if (
      endHour > startHour ||
      (endHour === startHour &&
        endDateTime.getMinutes() >= startDateTime.getMinutes())
    ) {
      return true; // Times are on the same day or sequential
    }
    return false; // End time is earlier than start time on the same day
  }

  compareTwoTimestamp(timestamp1: number, timestamp2: number): number {
    // Convert timestamps to milliseconds if they are in seconds
    const date1 = dayjs(timestamp1 * 1000);
    const date2 = dayjs(timestamp2 * 1000);
    const differenceInDays = Math.abs(date1.diff(date2, 'days'));
    return differenceInDays;
  }

  getCurrentEpoch(timezone?: string): number {
    const timezoneToUse = this._localStorage.get('time_zone') || timezone;
    const currentTime = dayjs().tz(timezoneToUse);
    return currentTime.unix(); //epoch time in seconds
  }

  getFormattedCurrentTime(timezone?: string): string {
    const currentEpoch = this.getCurrentEpoch(timezone);
    try {
      // Returns formatted time as 'hh:mm:ss A'
      return dayjs.unix(currentEpoch).format('LTS');
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid time';
    }
  }
  formatCollectionDate(
    unixTimestamp: number,
    format: string = 'DD MMMM YYYY'
  ): string {
    const timezone = this._localStorage.get('time_zone');
    const formattedDate = dayjs.unix(unixTimestamp).tz(timezone).format(format);
    return formattedDate;
  }

  formatDateWithoutTimezone(
    unixTimestamp: number,
    format: string = 'DD MMMM YYYY'
  ): string {
    const formattedDate = dayjs.unix(unixTimestamp).format(format);
    return formattedDate;
  }

  differenceInCalendarDays(
    dateLeft: string | Date,
    dateRight: string | Date
  ): number {
    return dayjs(dateLeft).diff(dayjs(dateRight), 'day');
  }

  differenceInCalendarMonths(
    dateLeft: string | Date,
    dateRight: string | Date
  ): number {
    return dayjs(dateLeft).diff(dayjs(dateRight), 'month');
  }

  isBefore(date1: string | Date, date2: string | Date): boolean {
    return dayjs(date1).isBefore(dayjs(date2));
  }

  subYears(date: string | Date, numberOfYears: number): dayjs.Dayjs {
    return dayjs(date).subtract(numberOfYears, 'year');
  }

  isOlderThanMonths(date: string | Date, months: number): boolean {
    const selectedDate = dayjs(date);
    const targetDate = dayjs().subtract(months, 'month');
    return selectedDate.isBefore(targetDate);
  }

  // Converts a UNIX timestamp to a localized date string in the specified or default timezone.
  convertTimestampToLocale(timeStamp: number, timezone?: string): string {
    if (!timeStamp) {
      console.error('Timestamp is missing or invalid');
      return 'Invalid date';
    }
    const effectiveTimezone =
      timezone?.trim() || this._localStorage.get('time_zone') || 'UTC';
    try {
      return dayjs.unix(timeStamp).tz(effectiveTimezone).format('LLL');
    } catch (error) {
      console.error('Invalid timezone or date provided', error);
      return 'Invalid date';
    }
  }

  isMatchingDate(
    dateToCheck: Date | string,
    compareBy: 'day' | 'month' = 'day'
  ): boolean {
    // Normalize both the current date and target date to the start of the day (00:00:00)
    const today = dayjs().startOf('day');
    const targetDate = dayjs(dateToCheck).startOf('day');

    if (compareBy === 'month') {
      // Compare only the month and year (ignoring the day)
      return today.isSame(targetDate, 'month');
    }

    // Default comparison by 'day' (checks both day and month)
    return today.isSame(targetDate, 'day');
  }

  getTimeDateFormatted(date) {
    return dayjs
      .tz(dayjs(date), this._localStorage.get('time_zone'))
      .format('DD/MM/YYYY | h:mm A');
  }

  getDateRange(type) {
    const timezone = this._localStorage.get('time_zone');
    const today = timezone ? dayjs().tz(timezone) : dayjs();
    const ranges = {
      today: { start: today, end: today },
      yesterday: {
        start: today.subtract(1, 'day'),
        end: today.subtract(1, 'day'),
      },
      last7days: {
        start: today.subtract(7, 'day'),
        end: today.subtract(1, 'day'),
      },
      last30days: {
        start: today.subtract(30, 'day'),
        end: today.subtract(1, 'day'),
      },
      last90days: {
        start: today.subtract(90, 'day'),
        end: today.subtract(1, 'day'),
      },
      last180days: {
        start: today.subtract(180, 'day'),
        end: today.subtract(1, 'day'),
      },
      lastyear: {
        start: today.subtract(365, 'day'),
        end: today.subtract(1, 'day'),
      },
      lastweek: {
        start: today.subtract(1, 'week').startOf('week'),
        end: today.subtract(1, 'week').endOf('week'),
      },
      thismonth: {
        start: today.startOf('month'),
        end: today.subtract(1, 'day'),
      },
      lastmonth: {
        start: today.subtract(1, 'month').startOf('month'),
        end: today.subtract(1, 'month').endOf('month'),
      },
    };

    const range = ranges[type] || { start: today, end: today };

    return {
      type,
      startDate: range.start.format('YYYY-MM-DD'),
      endDate: range.end.format('YYYY-MM-DD'),
    };
  }

  getToday() {
    return dayjs().tz(this._localStorage.get('time_zone')).format('YYYY-MM-DD');
  }

  calculateDaysBetween2Dates(start, end, texts?) {
    const a = dayjs(start);
    const b = dayjs(end);
    const x = b.diff(a, 'days') + 1;
    if (texts) {
      if (x == 0) return texts.ONE_DAY;
      else
        return `${texts.LAST.toLowerCase()} ${x} ${texts.DAYS.toLowerCase()}`;
    } else {
      if (x == 0) return 1;
      else return x;
    }
  }

  getEpochTime(time) {
    return new Date(time).getTime();
  }

  getCurrentTime() {
    return dayjs().tz(this._localStorage.get('time_zone')).format('HH:mm');
  }

  getCurrentDateMonth(date) {
    return dayjs(date)
      .locale(this._localStorage.get('locale'))
      .tz(this._localStorage.get('time_zone'))
      .format('dddd D[, ] MMMM');
  }

  getCurrentDateMonthFormat() {
    dayjs.locale(this._languageService.getLanguage());
    return dayjs()
      .tz(this._localStorage.get('time_zone'))
      .format('dddd D [, ] MMMM');
  }

  getCurremtTimein12() {
    return dayjs().format('hh:mm');
  }
  getCurrentAMPM() {
    return dayjs().format('A');
  }
  getCurrentDate2() {
    return dayjs().format('DD/MM/YYYY');
  }
  returnFormatedDate(date) {
    return dayjs(date).format('YYYY-MM-DD');
  }
  returnLastDateMonth() {
    const currentDate = new Date();
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    let lastMonthIndex = currentDate.getMonth() - 1;
    let year = currentDate.getFullYear();

    if (lastMonthIndex === -1) {
      // If the current month is January, set the last month to December of the previous year
      lastMonthIndex = 11; // December is at index 11
      year--;
    }

    const lastMonth = months[lastMonthIndex];
    return `${lastMonth} ${year}`;
  }
  getFormatedDate(date) {
    return dayjs(date).format('YYYY-MM-DD');
  }
  returnDateInMalasiya(epoch) {
    return dayjs
      .tz(dayjs.unix(epoch), this._localStorage.get('time_zone'))
      .format('D MMM YYYY hh:mm A');
  }
  returnSeconds(number, dateFilter) {
    let seconds = 0;

    switch (dateFilter.toLowerCase()) {
      case 'hours':
        seconds = +number * 3600; // 1 hour = 3600 seconds
        break;
      case 'days':
        seconds = +number * 86400; // 1 day = 86400 seconds
        break;
      case 'weeks':
        seconds = +number * 604800; // 1 week = 604800 seconds
        break;
      case 'minutes':
        seconds = +number * 60; // 1 minute = 60 seconds
        break;
      default:
        break;
    }
    return seconds;
  }
  returnDateInDDMMYYFormat(epoch) {
    return dayjs
      .tz(dayjs.unix(epoch), this._localStorage.get('time_zone'))
      .format('DD/MM/YY hh:mmA');
  }

  convert24HRFormat12HRFormat(time: string): string {
    const parsedTime = dayjs(time, ['HH:mm', 'HH:mm:ss', 'hh:mm A', 'hh:mm a']);
    return parsedTime.format('hh:mm A');
  }
  
  getReadableMoreDateFormat(date) {
    return dayjs(date).format('D MMM, YYYY');
  }
  getReadableMoreDateFormatUnix(date) {
    return dayjs.unix(date).format('D MMM, YYYY');
  }
  getCurrentDay() {
    return dayjs().format('dddd');
  }
  formatTime(timeString) {
    const time = dayjs(timeString);
    const formattedTime = time.format('HH:mm');
    return formattedTime;
  }
  getReadableDateFormat(date) {
    return dayjs(date).format('D MMM, YY');
  }
  convertMinutesToDate(time) {
    let x = this.convertMinuteToHour24format(time).split(':');
    let hh = parseInt(x[0]);
    let mm = parseInt(x[1]);
    let date = new Date();
    date.setHours(hh);
    date.setMinutes(mm);
    return date;
  }
  convertTimeToUTC(time) {
    const dateTime =
      dayjs
        .utc(time, 'hh:mm A')
        .subtract(dayjs().utcOffset(), 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';
    return dateTime;
  }
  convertMinuteToHour24format(mins) {
    let h: any = Math.floor(mins / 60);
    let m: any = mins % 60;
    h = h < 10 ? '0' + h : h;
    m = m < 10 ? '0' + m : m;
    return `${h}:${m}`;
  }
  subtractDaysToDate(date, qty) {
    return dayjs(date).subtract(qty, 'days').format('YYYY-MM-DD');
  }
  addDaysToDate(date, qty) {
    return dayjs(date).add(qty, 'days').format('YYYY-MM-DD');
  }
  convertStringifiedTimeToDateTime(time, customDate?) {
    let extractedTime = time.split(' ');
    let timeString = extractedTime[0].split(':');
    let hh = timeString[0];
    let mm = timeString[1];
    let meridian = extractedTime[1];
    if (hh === '12' && (meridian || '').toLowerCase() === 'am') {
      hh = '00';
    }
    if ((meridian || '').toLowerCase() === 'pm' && hh !== '12') {
      hh = +hh + 12;
    }
    let date = customDate ? new Date(customDate) : new Date();
    date.setHours(hh);
    date.setMinutes(mm);
    return date;
  }
  convertHoursToStandardFormat(hoursControl, setTime, duration) {
    let hours = hoursControl.value;
    hours = hours.replace(/^0+/, '');
    const hoursNumber = parseInt(hours, 10);
    if (
      (isNaN(hoursNumber) || hoursNumber < 0 || hoursNumber > setTime) &&
      duration === 'hours'
    ) {
      hours = '';
    } else if (hoursNumber > 0 && hoursNumber < 10) {
      hours = '0' + hours;
    } else if (hoursNumber >= 10 && hoursNumber <= setTime) {
      hours = hoursNumber.toString();
    } else if (
      (hoursNumber < 0 || hoursNumber > setTime) &&
      duration === 'minutes'
    ) {
      hours = '';
    } else if (isNaN(hoursNumber) && duration === 'minutes') {
      hours = '00';
    }
    return hours;
  }
  convertUTCtoTime(utcTime: string) {
    const dateTime = new Date(utcTime);
    let hours = dateTime.getHours();
    let meridian = 'AM';
    if (hours >= 12) {
      meridian = 'PM';
      if (hours > 12) {
        hours -= 12;
      }
    }
    if (hours === 0) {
      hours = 12;
    }
    const minutes = dateTime.getMinutes();
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}${meridian}`;
    return timeString;
  }
  convertDateTimeToStringifiedTime(date) {
    if (!date) return null;
    let hh = date.getHours();
    let mm = date.getMinutes();
    let meridiem = hh >= 12 ? 'PM' : 'AM';
    hh = hh > 12 ? hh - 12 : hh;
    return `${hh < 10 ? '0' + hh : hh}:${mm < 10 ? '0' + mm : mm} ${meridiem}`;
  }
  getLastDateOfMonthFormatted() {
    const currentDate = new Date();
    const lastDay = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    );

    const day = lastDay.getDate();
    const month = lastDay.toLocaleString('default', { month: 'long' });

    const formattedDate = `${this.addSuffixToDay(day)} ${month}`;

    return formattedDate;
  }
  addSuffixToDay(date) {
    const day = date;
    if (day >= 11 && day <= 13) {
      return `${day}th`;
    }
    switch (day % 10) {
      case 1:
        return `${day}st`;
      case 2:
        return `${day}nd`;
      case 3:
        return `${day}rd`;
      default:
        return `${day}th`;
    }
  }
  /*Funtion that returns item availabilty by epoch by comparing current epoch*/
  itemAvailabilityStatus(epoch = 0) {
    return Number(epoch) < dayjs().unix();
  }

  secToHHMMSS(sec) {
    const sec_num = parseInt(sec, 10);
    let hours: any = Math.floor(sec_num / 3600);
    let minutes: any = Math.floor((sec_num - hours * 3600) / 60);
    let seconds: any = sec_num - hours * 3600 - minutes * 60;

    if (hours < 10) {
      hours = '0' + hours;
    }
    if (minutes < 10) {
      minutes = '0' + minutes;
    }
    if (seconds < 10) {
      seconds = '0' + seconds;
    }
    return hours + ':' + minutes + ':' + seconds;
  }
  minuteToDayTime(min: any) {
    let hr = Math.floor(Number(min / 60));
    min = min - hr * 60;
    let meridian = 'AM';
    let hrStr = '';
    let minStr = '';
    hr = hr > 24 ? hr - 24 : hr;
    if (hr == 12) {
      meridian = 'PM';
    }
    if (hr == 24 || hr == 0) {
      hr = 12;
    } else if (hr > 12) {
      hr = hr - 12;
      meridian = 'PM';
    }
    if (hr < 10) {
      hrStr = '0' + hr;
    } else {
      hrStr = hr.toString();
    }
    if (min < 10) {
      minStr = '0' + min;
    } else {
      minStr = min.toString();
    }
    return hrStr + ':' + minStr + '' + meridian;
  }

  // Function to get epoch timestamp from date, time, and timezone
  getTimeStampEpoch(timestampParams: TimeStampInterface): number {
    try {
      const { date, time, timezone } = timestampParams;

      // Set default timezone from local storage if not provided specifically
      const defaultTimezone = this._localStorage.get('time_zone');

      // Use provided timezone if available, otherwise use default timezone
      const timezoneToUse = timezone || defaultTimezone;

      const timestampInMiliSecond = dayjs
        .tz(`${date} ${time}`, timezoneToUse)
        .valueOf();
      return Math.floor(timestampInMiliSecond / 1000); // timestamp in seconds
    } catch (error) {
      console.error('Error occurred while converting timestamp:', error);
      return null;
    }
  }

  getMonthDateYearFormat(date: Date): string {
    return dayjs(date).format('MMM D, YYYY');
  }

  // Helper method to format Date object to DD/MM/YY string
  formatDateToString(date: Date): string {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  }

  // Helper method to format Date object to HH:mm string
  formatTimeToString(time: Date): string {
    if (!time) return '';
    const hours = time.getHours().toString().padStart(2, '0');
    const minutes = time.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  // Function to convert minutes to time in HH:mm format
  minutesToTime(minutes: number): string {
    return dayjs.utc().startOf('day').add(minutes, 'minutes').format('hh:mm A');
  }

  convertDateFormat(date) {
    return dayjs(date, 'YYYY-MM-DD').format('DD MMM YYYY');
  }
  convertToShortDate(date) {
    return dayjs(date, 'YYYY-MM-DD').format('D MMM');
  }
  convertShortDate(date) {
    return dayjs(date, 'YYYY-MM-DD').format('DD MMM');
  }
  getDay(date) {
    return dayjs(date, 'YYYY-MM-DD').format('ddd');
  }
  getBeforeDate(date, diff) {
    const x = dayjs(date).subtract(diff, 'days').format('DD MMM YYYY');
    return x;
  }

  restTimeZoneOffset() {
    const timezone = this._localStorage.get('time_zone');
    return dayjs(new Date().getTime()).tz(timezone).utcOffset() * 60;
  }

  getTodaysDate() {
    return dayjs().format('YYYY-MM-DD');
  }

  getDateAfterXDays(days: number) {
    return dayjs().add(days, 'days').format('YYYY-MM-DD');
  }
  isFutureDate(dateToCheck: Date | string): boolean {
    // Normalize date to start of the day (00:00:00)
    const today = dayjs().startOf('day');
    const targetDate = dayjs(dateToCheck).startOf('day');

    return targetDate.isAfter(today);
  }
  returnDateInFormat(date: string, time: string): string {
    try {
      // Strict parse the date and time strings separately
      const datePart = dayjs(date || dayjs(), 'DD/MM/YY', true);
      const timePart = dayjs(time || dayjs(), 'HH:mm', true);
      // Validate both date and time
      if (!datePart.isValid() || !timePart.isValid()) {
        throw new Error('Invalid date or time format');
      }

      // Combine the datePart with the extracted timePart
      const combinedDateTime = datePart
        .set('hour', timePart.hour())
        .set('minute', timePart.minute())
        .set('second', timePart.second());

      return combinedDateTime.format('MMM DD YYYY hh:mmA');
    } catch (error) {
      console.error(`Error formatting date and time: ${error?.message}`);
      return 'Invalid Date/Time';
    }
  }

  returnScheduleEpoch(date: string, time: string): number | null {
    try {
      // Parse the date and time separately with strict validation
      const datePart = dayjs(date, 'DD/MM/YY', true);
      const timePart = dayjs(time, 'HH:mm', true);

      // Validate both date and time parts
      if (!datePart.isValid() || !timePart.isValid()) {
        throw new Error('Invalid date or time format');
      }

      // Combine datePart with the extracted timePart
      const combinedDateTime = datePart
        .set('hour', timePart.hour())
        .set('minute', timePart.minute())
        .set('second', 0); // Set seconds to 0 to standardize output

      // Return the Unix timestamp (epoch time in seconds)
      return combinedDateTime.unix();
    } catch (error) {
      console.error(
        `Error converting date and time to epoch: ${error?.message}`
      );
      return null;
    }
  }

  convertDateTimeToEpoch(dateTime: {
    date: Date;
    time?: { hour: number; minute: number; period: 'AM' | 'PM' } | null;
  }): number | null {
    try {
      if (!dateTime?.date) {
        console.error('Invalid date provided');
        return null;
      }

      // 1. Parse the base date in stored timezone
      const baseDate = dayjs.tz(
        dateTime.date,
        this._localStorage.get('time_zone')
      );

      // 2. Handle missing/null time (defaults to 12:00 AM)
      let hours = 0;
      let minutes = 0;
      if (dateTime.time) {
        hours = dateTime.time.hour;
        minutes = dateTime.time.minute;

        // Convert 12h time to 24h format
        if (dateTime.time.period === 'PM' && hours !== 12) {
          hours += 12;
        } else if (dateTime.time.period === 'AM' && hours === 12) {
          hours = 0;
        }
      }

      // 3. Create final datetime object
      const finalDateTime = baseDate
        .hour(hours)
        .minute(minutes)
        .second(0)
        .millisecond(0);

      // 4. Return epoch in seconds
      return finalDateTime.unix();
    } catch (error) {
      console.error('Error converting datetime to epoch:', error);
      return null;
    }
  }

  convertEpochToDateTime(epoch: number): {
    date: Date;
    time: { hour: number; minute: number; period: 'AM' | 'PM' };
  } {
    try {
      if (!epoch) {
        console.error('Invalid epoch timestamp provided');
        return {
          date: new Date(),
          time: { hour: 12, minute: 0, period: 'AM' },
        };
      }

      // Get user's timezone from local storage (default to 'UTC' if not found)
      const userTimeZone = this._localStorage.get('time_zone') || 'UTC';

      // Convert epoch to dayjs object in the user's timezone
      const dateTime = dayjs.unix(epoch).tz(userTimeZone);

      // Extract date and time components
      const date = dateTime.toDate();
      let hours = dateTime.hour();
      const minutes = dateTime.minute();
      const period = hours >= 12 ? 'PM' : 'AM';

      // Convert 24-hour time to 12-hour format
      if (hours > 12) {
        hours -= 12;
      } else if (hours === 0) {
        hours = 12;
      }

      return { date, time: { hour: hours, minute: minutes, period } };
    } catch (error) {
      console.error('Error converting epoch to date-time:', error);
      return { date: new Date(), time: { hour: 12, minute: 0, period: 'AM' } };
    }
  }

  // Format Unix timestamp to readable time in 12-hour format (HH:MM AM/PM)
  formatUnixTimestampToTime(timestamp: number): string {
    if (!timestamp) return '';
    
    const timezone = this._localStorage.get('time_zone') || 'UTC';
    
    try {
      return dayjs.unix(timestamp).tz(timezone).format('hh:mm A');
    } catch (error) {
      console.error('Error formatting timestamp to time:', error);
      return '';
    }
  }
}
